import { observer } from 'mobx-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Block, Heading, Label, Paragraph, Text } from 'suomifi-ui-components'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { LastNameInfoBlock } from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSignerLastnameConfirmationStep.sc'
import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'

const AetLastnameConfirmationStep: StepComponent = observer(
  forwardRef(function AetLastnameConfirmationStep({ titleId }, _ref) {
    const form = useAetSignerForm()
    if (!form.aetTodistuspyynto) {
      return <></>
    }
    const {
      aetTodistuspyynto: {
        orderer: { firstNames: spouseAFirstNames, lastName: spouseALastName },
        aetLisaosa: {
          spouseAKeepingCurrentLastName,
          spouseANewLastName,
          spouseBKeepingCurrentLastName,
          spouseBNewLastName,
        },
      },
      orderer: { firstNames, lastName },
    } = form

    const ordererLastNameAfterMarriage = finalEditableFieldValue(
      spouseAKeepingCurrentLastName
    )
      ? finalEditableFieldValue(spouseALastName) || ''
      : finalEditableFieldValue(spouseANewLastName) || ''

    const signerLastNameAfterMarriage = finalEditableFieldValue(
      spouseBKeepingCurrentLastName
    )
      ? finalEditableFieldValue(lastName) || ''
      : finalEditableFieldValue(spouseBNewLastName) || ''

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetSignerLastnameConfirmationMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetSignerLastnameConfirmationMainDescription_paragraphs" />
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetSignerLastnameConfirmationUpcomingLastNamesTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetSignerLastnameConfirmationUpcomingLastNamesDescription_paragraphs" />
          <SpouseLastNameInfo
            lastNameAfterMarriage={ordererLastNameAfterMarriage}
            firstNames={finalEditableFieldValue(spouseAFirstNames) ?? '-'}
            spouse="A"
          />
          <SpouseLastNameInfo
            lastNameAfterMarriage={signerLastNameAfterMarriage ?? '-'}
            firstNames={
              firstNames ? finalEditableFieldValue(firstNames) ?? '-' : '-'
            }
            spouse="B"
          />
        </Block>
      </FormContainer>
    )
  })
)

export default AetLastnameConfirmationStep

const SpouseLastNameInfo: React.FC<{
  lastNameAfterMarriage: string
  firstNames: string
  spouse: 'A' | 'B'
}> = ({ lastNameAfterMarriage, firstNames, spouse }) => {
  const { t } = useTranslation()
  const nameParagraphId = `spouse-${spouse}-name`
  return (
    <LastNameInfoBlock>
      <Paragraph>
        <Text variant="bold">
          {t(`aetSignerLastnameConfirmationSpouseSubTitle${spouse}`)}
        </Text>
      </Paragraph>
      <Label htmlFor={nameParagraphId}>
        <LocalizedString stringKey="aetSignerLastnameConfirmationLastNameAfterMarriage" />
      </Label>
      <Paragraph id={nameParagraphId}>
        <Text variant="bold">{lastNameAfterMarriage}</Text>
        <Text>{`, ${firstNames}`}</Text>
      </Paragraph>
    </LastNameInfoBlock>
  )
}
